import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, List, Popover, Tooltip, message } from 'antd';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import {
	fetchAllContractorWOUsersForSuppliers,
	fetchAllUsersForBuyers,
	fetchAllUsersForSuppliers,
} from '../../thunks/work_orders_subscribers_thunks';
import { getObjectValues, isSubContractor, nullSafeGet } from '../../utils/DataAccessUtils';
import { imageMimeTypes, imageUploadValidation } from '../../utils/ImageUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { MentionsInput, Mention } from 'react-mentions';
import './index.less';
import ChatUploader from './ChatUploader';
import { UserName } from '../name_component/user/UserNameComponent';
import { getContractorOrWorkOrderId } from '../../utils/WorkOrderUtils';
const videoMimeTypes = [
	'video/3gpp', //.3gp
	'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r, .m4v
	'video/mpeg', //.m1v
	'video/ogg', //.ogg
	'video/quicktime', //.mov, .qt
	'video/webm', //.webm
	'video/x-m4v', //.m4v
	'video/ms-asf', //.asf, .wma, .wmv
	'video/x-ms-wmv', //.wmv
	'video/x-msvideo', //.avi
];

const ChatInput = ({
	handlePhotoUploadChange,
	handleStartVideoCall,
	fetchUsers,
	workOrder,
	currentUser,
	token,
	handleVideoUploadChange,
	onChange,
	value,
	fetchSupplierUsers,
	handleSubmit,
	photoFileList = [],
	videoFileList = [],
	showMultiMediaOptions = true,
}) => {
	const userType = currentUser.userType;

	const workOrderId = useMemo(() => getContractorOrWorkOrderId(workOrder), [workOrder]);
	const loadUsers = useCallback(
		(key: string, cb) => {
			// WorkAround: MentionsInput calls this funtion everytime. if space count is more than 1, it will not call the api.
			// https://github.com/signavio/react-mentions/issues/247#issuecomment-516529736
			const count = key.split(' ').length - 1;
			if (count < 2) {
				if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
					fetchSupplierUsers(key, workOrderId).then((data) =>
						cb(
							getObjectValues(data).map((el) => ({
								...el,
								id: el.email,
								display: `${el.nameGiven} ${el.nameFamily}`,
							}))
						)
					);
				} else {
					fetchUsers(key, workOrderId).then((data) =>
						cb(
							getObjectValues(data).map((el) => ({
								...el,
								id: el.email,
								display: `${el.nameGiven} ${el.nameFamily}`,
							}))
						)
					);
				}
			} else {
				cb([]);
			}
		},
		[currentUser.userType, fetchSupplierUsers, fetchUsers, workOrderId]
	);

	const debounceloadUsers = useCallback(debounce(loadUsers, 250), [loadUsers]);

	const onSearch = useCallback(
		(search: string, cb) => {
			debounceloadUsers(search, cb);
		},
		[debounceloadUsers]
	);
	function beforeVideoUpload(file) {
		const isVideo = videoMimeTypes.indexOf(file.type) !== -1;
		if (!isVideo) {
			message.error("This file either isn't a video or is not in a file type we support.");
		}
		return isVideo;
	}

	return (
		<div>
			<div className={`mx-4 flex flex-row `}>
				<div className="flex items-end bg-zinc-50  px-3 pb-2">
					{showMultiMediaOptions ? (
						<>
							<a
								href={`https://live.useopenwrench.com/?roomName=${nullSafeGet(
									'facility.buyerCompanyId',
									currentUser
								)}-${getContractorOrWorkOrderId(
									workOrder
								)}&authToken=${token}&userType=${userType}`}
								target="_blank"
								rel="noreferrer"
							>
								<Tooltip title="Start video call">
									<Button
										onClick={handleStartVideoCall}
										shape="circle"
										style={{ border: 'none', marginRight: 4 }}
									>
										<i style={{ fontSize: 20 }} className="icons8-font icons8-video-call"></i>
									</Button>
								</Tooltip>
							</a>

							<ChatUploader
								name="file"
								multiple={true}
								showUploadList={false}
								beforeUpload={imageUploadValidation}
								onChange={handlePhotoUploadChange}
								//onPreview={this.handlePreview}
								id={workOrderId}
								accept={imageMimeTypes}
								fileList={photoFileList}
							>
								<Tooltip title="Upload photo">
									<Button style={{ border: 'none', marginRight: 4 }} shape="circle">
										<i style={{ fontSize: 20 }} className="icons8-font icons8-compact-camera"></i>
									</Button>
								</Tooltip>
							</ChatUploader>
							<ChatUploader
								name="file"
								showUploadList={false}
								beforeUpload={beforeVideoUpload}
								onChange={handleVideoUploadChange}
								accept={videoMimeTypes}
								id={workOrderId}
								fileList={videoFileList}
							>
								<Tooltip title="Upload video">
									<Button style={{ border: 'none' }} shape="circle">
										<i style={{ fontSize: 20 }} className="icons8-font icons8-video"></i>
									</Button>
								</Tooltip>
							</ChatUploader>
						</>
					) : null}
				</div>
				<div className="w-full">
					<MentionsInput
						placeholder="Add a note to this work order."
						forceSuggestionsAboveCursor
						value={value}
						onChange={(e, value) => onChange(value)}
						className="chat-input  ant-input"
						allowSpaceInQuery={true}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								e.stopPropagation();
								e.nativeEvent.stopImmediatePropagation();
								handleSubmit(e);
							}
						}}
					>
						<Mention
							trigger="@"
							data={onSearch}
							className="bg-[#d2d9de]"
							renderSuggestion={(user, search, highlightedDisplay, index, focused) => {
								return (
									<div className="flex flex-row p-2 px-4">
										<UserName contact={user} onClick={() => {}} />
									</div>
								);
							}}
						/>
					</MentionsInput>
				</div>
				<div className="flex items-end bg-zinc-50 px-3 pb-2">
					<Tooltip title="Send">
						<Button htmlType="submit" shape="circle" style={{ border: 'none' }}>
							<i style={{ fontSize: 20 }} className="icons8-font icons8-paper-airplane"></i>
						</Button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	searched_subscribers: state.searched_subscribers,
	token: state.session.token,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchUsers: (search, woId, params) => dispatch(fetchAllUsersForBuyers(search, woId, params)()),
	fetchSupplierUsers: (search, woId, params) =>
		dispatch(
			nullSafeGet('workOrder.contractorWo', ownProps)
				? fetchAllContractorWOUsersForSuppliers(
						search,
						nullSafeGet('workOrder.contractorWo.id', ownProps),
						params
				  )()
				: fetchAllUsersForSuppliers(search, woId, params)()
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatInput);
